"use client";

import { useAuth } from "@/contexts/AuthContext";
import useRecaptcha from "@/hooks/useRecaptcha";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useEffect, useState } from "react";

import AppleSignInAndUp from "./AppleSignInAndUp";
import GoogleSignInAndUp from "./GoogleSignInAndUp";
import MetaSignInAndUp from "./MetaSignInAndUp";

function loadScript(id, src) {
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.src = src;
  script.async = true;
  script.onerror = () => {
    script.dataset.loadFailed = "true";
  };
  document.head.appendChild(script);
}

const PROVIDERS = {
  GOOGLE: "google",
  META: "meta",
  APPLE: "apple",
};

const SocialLogins = ({
  metaButtonText,
  googleButtonText,
  appleButtonText,
  invitationCode = null,
}) => {
  const { session, register } = useAuth();
  const { getCaptchaToken } = useRecaptcha();
  const t = useTranslationDetector();

  const [loadingProvider, setLoadingProvider] = useState(null);
  const [errorProvider, setErrorProvider] = useState(null);

  useEffect(() => {
    loadScript("google-platform", "https://accounts.google.com/gsi/client");
    loadScript("facebook-sdk", "https://connect.facebook.net/en_US/sdk.js");
    loadScript(
      "apple-signin",
      "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js",
    );
  }, []);

  const signUp = async (data, provider) => {
    setErrorProvider(null);
    setLoadingProvider(provider);

    try {
      const RecaptchaKey = await getCaptchaToken("signup");
      const requestParams = { ...data, RecaptchaKey };
      if (invitationCode) {
        requestParams.InvitationCode = invitationCode;
      }

      const response = await register(requestParams);

      if (!response?.success) {
        setErrorProvider({
          provider,
          message: response?.error,
        });
      }
    } catch (error) {
      setErrorProvider({
        provider,
        message: t(
          `${error?.response?.data?.Type}.${error?.response?.data?.Name}`,
        ),
      });
    } finally {
      setLoadingProvider(null);
    }
  };

  const onMetaSignIn = (userInfo) => {
    signUp(
      {
        LanguageCode: session?.LanguageCode,
        CountryCode: session?.CountryCode,
        CurrencyCode: session?.CurrencyCode,
        FirstName: userInfo?.firstName,
        LastName: userInfo?.lastName,
        Email: userInfo?.email,
        AuthenticationType: 1,
        ExternalKey: userInfo?.id,
      },
      PROVIDERS.META,
    );
  };

  const onGoogleSignIn = (data) => {
    signUp(
      {
        Email: data?.email,
        AuthenticationType: 2,
        ExternalKey: data?.token,
      },
      PROVIDERS.GOOGLE,
    );
  };

  const onAppleSignIn = (data) => {
    if (!data.error) {
      signUp(
        {
          Email: data?.email,
          AuthenticationType: 3,
          ExternalKey: data?.appleUserId,
        },
        PROVIDERS.APPLE,
      );
    } else {
      setErrorProvider({ provider: PROVIDERS.APPLE, message: data.error });
    }
  };

  return (
    <div className="flex w-full flex-col gap-3">
      <div>
        <GoogleSignInAndUp
          buttonText={googleButtonText || t("common.googleSignUp")}
          callback={onGoogleSignIn}
          loading={loadingProvider === PROVIDERS.GOOGLE}
        />
        {errorProvider?.provider === PROVIDERS.GOOGLE && (
          <p className="mt-1 text-sm text-red-600">{errorProvider.message}</p>
        )}
      </div>

      <div>
        <MetaSignInAndUp
          buttonText={metaButtonText || t("common.metaSignUp")}
          callback={onMetaSignIn}
          loading={loadingProvider === PROVIDERS.META}
        />
        {errorProvider?.provider === PROVIDERS.META && (
          <p className="mt-1 text-sm text-red-600">{errorProvider.message}</p>
        )}
      </div>

      <div>
        <AppleSignInAndUp
          buttonText={appleButtonText || t("common.appleSignUp")}
          callback={onAppleSignIn}
          loading={loadingProvider === PROVIDERS.APPLE}
        />
        {errorProvider?.provider === PROVIDERS.APPLE && (
          <p className="mt-1 text-sm text-red-600">{errorProvider.message}</p>
        )}
      </div>
    </div>
  );
};

export default SocialLogins;
